import { FC, useContext, useMemo, useRef } from "react";
import { ColumnConfig, WafflesPaginationProps } from "@app/shared/waffles";
import ProviderNetworkTypeBadge from "@app/shared/ProviderNetworkTypeBadge";
import { sortProviderNetworkTypes } from "@app/shared/enums/providerNetworkTypeEnum";
import ProviderActionStatusBadge from "@app/shared/ProviderActionStatusBadge";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import { WafflesThemes } from "@app/shared/waffles/exports";
import { ProviderBadgeStyle } from "@app/projects/enumerators";
import WaffleRowTooltip from "@app/shared/waffles/WaffleRowTooltip";
import PageSection from "@app/shared/PageSection";
import { QESButton } from "@app/shared/qes-standards/Button";
import { Waffles } from "@app/shared/waffles/Waffles";
import { ProviderHubContext } from "@app/projects/network-assessment/provider-hub/contexts/ProviderHubContext";
import { GapImpactPaginationResult, ProviderHubContractEntityProviders, ProviderHubContractEntityProvidersTableData } from "@app/projects/network-assessment/provider-hub/interfaces";
import { ProviderProfileReferrerEnum } from "@app/projects/network-assessment/provider-hub/provider-profile/interfaces/providerProfileReferrerEnum";
import ProviderHubService from "@app/projects/network-assessment/provider-hub/providerHubService";
import { ProviderScoreColumnHeader } from "@app/projects/network-assessment/provider-hub/ProviderScoreColumnHeader";
import { NO_ACTIONS_APPLIED_LABEL, formatCountiesImpacted, formatGapImpactCount, formatScore, formatSpecialtyWithCode, sortPredictedSpecialtyNames } from "@app/projects/network-assessment/provider-hub/utility";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import Features from "@app/feature-flags/features";

interface OrganizationProfileTableProps {
    organizationProviderTableData: GapImpactPaginationResult<ProviderHubContractEntityProviders>;
    pageIndex: number;
    pageSize: number;
    resetToFirstPage: boolean;
    setOrganizationProviderTableData: React.Dispatch<React.SetStateAction<GapImpactPaginationResult<ProviderHubContractEntityProviders>>>;
    setPageIndex: React.Dispatch<React.SetStateAction<number>>;
    setPageSize: React.Dispatch<React.SetStateAction<number>>;
    setResetToFirstPage: React.Dispatch<React.SetStateAction<boolean>>;
}

const OrganizationProfileTable: FC<React.PropsWithChildren<OrganizationProfileTableProps>> = (props: OrganizationProfileTableProps) => {
    const projectContext = useContext(CountyProjectContext);
    const providerHubOrganizationContext = useContext(ProviderHubContext);
    const providerHubService = useRef(new ProviderHubService()).current;
    const featureFlagContext = useContext(FeatureFlagContext);

    const useRegionStatusBreakdown = useMemo<boolean>(() => {
        return props.organizationProviderTableData.useGapsByRegionStatus === true;
    }, [props.organizationProviderTableData]);

    /* oxlint-disable sort-keys */
    const providerHubProvidersTableColumnConfig: ColumnConfig = {
        nameElement: {
            displayText: "Name",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_organization-table-provider-name",
            noSort: true
        },
        identifier: {
            displayText: "Identifier",
            sizeContentToFit: true,
            sticky: true,
            testId: "provider-hub_organization-table-identifier",
            noSort: true
        },
        providerNetworkTypes: {
            displayText: "Provider Source",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-provider-source",
            tooltip: "Indicates the original database from which the provider data was obtained",
            noSort: true
        },
        providerActionStatuses: {
            displayText: "Status",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-status",
            tooltip: "Indicates any actions that have been applied to either a specific access point or across access points for an organization",
            noSort: true
        },
        affiliationStrength: {
            displayText: "Affiliation Strength",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-affiliationstrength",
            tooltip: "Indicates the strength of the affiliation",
            noSort: true
        },
        gapImpact: {
            displayText: "Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-gap-impact",
            tooltip: "Indicates the distinct number of gaps that the provider closes",
            noSort: true,
            hidden: useRegionStatusBreakdown
        },
        activeGapImpact: {
            displayText: "Active Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-active-gap-impact",
            tooltip: "Count of gaps closeable in Active service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        pendingGapImpact: {
            displayText: "Pending Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-pending-gap-impact",
            tooltip: "Count of gaps closeable in Pending service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        approvedGapImpact: {
            displayText: "Approved Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-approved-gap-impact",
            tooltip: "Count of gaps closeable in Approved service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        opportunityGapImpact: {
            displayText: "Opportunity Gap Impact",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-opportunity-gap-impact",
            tooltip: "Count of gaps closeable in Opportunity-to-expand service area counties",
            noSort: true,
            hidden: !useRegionStatusBreakdown
        },
        specialtiesImpacted: {
            displayText: "Specialty Groups Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-specialty",
            noSort: true
        },
        countiesImpacted: {
            displayText: "Counties Impacted",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-county",
            noSort: true
        },
        accessPoints: {
            displayText: "Access Points",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-filtered-oonsp",
            noSort: true
        },
        filteredQuality: {
            displayText: "Effectiveness (Quality)",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-filtered-quality",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        filteredCost: {
            displayText: "Efficiency (Cost)",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-filtered-cost",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        totalClaims: {
            displayText: "Total Claims",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-filteredclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        medicareClaims: {
            displayText: "Medicare Claims",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-medicareclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        medicaidClaims: {
            displayText: "Medicaid Claims",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-medicaidclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        commercialClaims: {
            displayText: "Commercial Claims",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-commercialclaims",
            hidden: projectContext.project.showPciData === false,
            noSort: true
        },
        vaClaims: {
            displayText: "VA Claims",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-vaclaims",
            hidden: projectContext.project.showVaClaimsVolumeColumn === false,
            noSort: true
        },
        tricareClaims: {
            displayText: "TRICARE Claims",
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-tricareclaims",
            hidden: projectContext.project.showTricareClaimsVolumeColumn === false,
            noSort: true
        },
        motiveAppropriateness: {
            displayElement: <ProviderScoreColumnHeader variant="motive" />,
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-motive",
            noSort: true,
            hidden: projectContext.project.showMotiveAppropriatenessScore === false
        },
        careJourneyCostScore: {
            displayElement: <ProviderScoreColumnHeader variant="careJourneyCost" />,
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-carejourneycost",
            noSort: true,
            hidden: projectContext.project.showCareJourneyScores === false
        },
        careJourneyQualityScore: {
            displayElement: <ProviderScoreColumnHeader variant="careJourneyQuality" />,
            sizeContentToFit: true,
            testId: "provider-hub_organization-table-carejourneyquality",
            noSort: true,
            hidden: projectContext.project.showCareJourneyScores === false
        },
        specialtyPrediction: {
          displayElement: (
            <div style={{ alignItems: "center", display: "flex" }}>
              {"Specialty Prediction"}
            </div>
          ),
          sizeContentToFit: true,
          testId: "provider-hub_organization-table-specialty-prediction",
          tooltip: null,
          noSort: true,
          hidden: !featureFlagContext.enabledFeatureFlags.has(Features.NETWORK_PERFORMANCE_SPECIALTY_PREDICTION_MODEL)
        }
    }
    /* oxlint-enable sort-keys */

    const handlePageSizeChange = (pageSize: number) => {
        props.setPageSize(pageSize);
        props.setPageIndex(1);
    }

    const handlePageChange = async (newPage: number) => {
        if (!props.resetToFirstPage) {
            props.setPageIndex(newPage);
        }
        if (props.resetToFirstPage) {
            props.setResetToFirstPage(false);
        }
    };

    const handleProviderClick = async (provider: ProviderHubContractEntityProviders) => {
        const selectedProviderDetails = await providerHubService.getServicingProviderDetails(projectContext?.project?.projectId, providerHubOrganizationContext.selectedOrganization.projectRunConfigurationId, provider.servicingProviderKey);
        providerHubOrganizationContext.navigateToProviderProfile(selectedProviderDetails, ProviderProfileReferrerEnum.OrganizationProfile);
    }

    /* oxlint-disable sort-keys */
    const providersTableData: ProviderHubContractEntityProvidersTableData[] = useMemo<ProviderHubContractEntityProvidersTableData[]>(() => {

        let data: ProviderHubContractEntityProvidersTableData[] = [];
        data = props.organizationProviderTableData.items.map((provider) => {
            return {
                key: provider.servicingProviderKey,
                nameElement: <QESButton
                    qesType="link"
                    qesTestId={`provider-hub-provider-view__provider--${provider.name}`}
                    onClick={() => { handleProviderClick(provider) }}
                    className="demibold"
                >
                    {provider.name ?? "---"}
                </QESButton>,
                name: provider.name ?? "---",
                identifier: provider.npi,
                providerNetworkTypes: (
                    <>
                        {sortProviderNetworkTypes(provider.providerNetworkTypes).map((networkType) =>
                            <ProviderNetworkTypeBadge badgeStyle={ProviderBadgeStyle.Outline} key={networkType} providerNetworkType={networkType} />
                        )}
                    </>
                ),
                providerActionStatuses: provider.providerActionStatuses.length > 0 ? (
                    <>
                        {provider.providerActionStatuses.map((status, index) =>
                            <ProviderActionStatusBadge badgeStyle={ProviderBadgeStyle.Dot} key={index} status={status} />
                        )}
                    </>
                ) : (
                    <>{NO_ACTIONS_APPLIED_LABEL}</>
                ),
                filteredStatuses: provider.providerActionStatuses.map(status => status),
                affiliationStrength: Math.round((provider.affiliationStrength ?? 0) * 100),
                gapImpact: formatGapImpactCount(provider.gapsClosed),
                activeGapImpact: formatGapImpactCount(provider.activeGapsClosed),
                pendingGapImpact: formatGapImpactCount(provider.pendingGapsClosed),
                approvedGapImpact: formatGapImpactCount(provider.approvedGapsClosed),
                opportunityGapImpact: formatGapImpactCount(provider.opportunityGapsClosed),
                specialtiesImpacted: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={provider.specialtiesImpacted?.map(formatSpecialtyWithCode)}
                    numberOfElementsPerRow={3} />,
                countiesImpacted: <WaffleRowTooltip
                    containerClassName="provider-hub-waffle-tooltip"
                    elements={formatCountiesImpacted(provider.countiesImpacted)}
                    numberOfElementsPerRow={3} />,
                accessPoints: provider.accessPointCount,
                filteredQuality: formatScore(provider.effectivenessRank),
                filteredCost: formatScore(provider.efficiencyRank),
                totalClaims: provider.totalClaimVolume ?? '---',
                medicareClaims: provider.medicareClaimVolume ?? '---',
                medicaidClaims: provider.medicaidClaimVolume ?? '---',
                commercialClaims: provider.commercialClaimVolume ?? '---',
                vaClaims: provider.vaClaimVolume ?? '---',
                tricareClaims: provider.tricareClaimVolume ?? '---',
                motiveAppropriateness: formatScore(provider.motiveAppropriateness),
                careJourneyCostScore: formatScore(provider.careJourneyCost),
                careJourneyQualityScore: formatScore(provider.careJourneyQuality),
                specialtyPrediction: <WaffleRowTooltip
                  containerClassName="provider-hub-waffle-tooltip"
                  elements={sortPredictedSpecialtyNames(provider.predictedSpecialtyNames)}
                  numberOfElementsPerRow={3}
                  separator=", " />,
            }
        });

        return data;

    }, [props.organizationProviderTableData]);
    /* oxlint-enable sort-keys */
    const paginationProps: WafflesPaginationProps = {
        changePage: (pageNumber: number) => handlePageChange(pageNumber),
        enableBackendPagination: true,
        handlePageSizeChange: (pageSize: number) => handlePageSizeChange(pageSize),
        pageSizeOptions: [50, 100, 150],
        recordsPerPage: props.pageSize,
        resetToFirstPage: props.resetToFirstPage,
        showPageSizeSelector: true,
        totalRecords: props.organizationProviderTableData.totalCount
    }

    return (
        <PageSection>
            <>
                <Waffles<ProviderHubContractEntityProvidersTableData>
                    columns={providerHubProvidersTableColumnConfig}
                    data={providersTableData}
                    totalNumberOfRecords={props.organizationProviderTableData.totalCount}
                    enablePagination={true}
                    emptyTableMessage="No matching provider results found. Please start a new search"
                    fullWidth={true}
                    paginationProps={paginationProps}
                    testId="provider-hub__organization-table"
                    theme={WafflesThemes.ContractEntityDetails}
                    disableStickyHeader={true}
                    variant="frozen-border"
                />
            </>
        </PageSection>
    )
}

export default OrganizationProfileTable;